import { Popover as ChakraPopover, Portal } from "@chakra-ui/react";
import { ReactNode } from "react";
import { getThemeColor } from "theme/colors";

export type PopoverPlacement =
  | "top"
  | "top-start"
  | "top-end"
  | "bottom"
  | "bottom-start"
  | "bottom-end";

interface PopoverProps {
  /** Element that toggles the popover (rendered via `asChild`). */
  trigger: ReactNode;
  children: ReactNode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Preferred placement. Chakra/floating-ui handles portal rendering (never
   * clipped by `overflow: hidden`) and auto-flips to the opposite side when
   * there isn't room. Defaults to opening above the trigger.
   */
  placement?: PopoverPlacement;
  /** Gap in px between trigger and content. */
  gutter?: number;
  width?: string | number;
}

/**
 * Thin wrapper around Chakra v3's Popover. Keeps the Chakra import in the
 * `components/ui` layer and applies the project's theme tokens to the content.
 */
export const Popover = ({
  trigger,
  children,
  open,
  onOpenChange,
  placement = "top-end",
  gutter = 8,
  width,
}: PopoverProps) => {
  return (
    <ChakraPopover.Root
      open={open}
      onOpenChange={(e: { open: boolean }) => onOpenChange(e.open)}
      positioning={{ placement, gutter }}
    >
      <ChakraPopover.Trigger asChild>{trigger}</ChakraPopover.Trigger>
      <Portal>
        <ChakraPopover.Positioner>
          <ChakraPopover.Content
            width={width}
            bg={getThemeColor("card.bg")}
            borderWidth="1px"
            borderColor={getThemeColor("border.default")}
            borderRadius="lg"
            boxShadow="lg"
          >
            <ChakraPopover.Body p={4}>{children}</ChakraPopover.Body>
          </ChakraPopover.Content>
        </ChakraPopover.Positioner>
      </Portal>
    </ChakraPopover.Root>
  );
};

export default Popover;
